import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {getNonBatchContainerContents} from 'constants/SampleConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {StorableInput} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {InventoryTableRowMetadata} from 'notebook/integrations/samples/InventoryStructuredTableAdapter';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  isContainersStructuredTableColumn,
  isExistingBioentityStructuredTableColumn,
  isMeasurementAmountStructuredTableColumn,
  isParentStorableStructuredTableColumn,
  isPositionStructuredTableColumn,
  isTagValueStructuredTableColumn,
  isUnitSelectorStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {
  BulkInventoryContainerInfoQuery,
  BulkInventoryContainerInfoQueryVariables,
} from 'notebook/structured-tables/row-updaters/__generated/BulkInventoryContainerInfoQuery';
import {
  BulkInventoryPlateWellInfoQuery,
  BulkInventoryPlateWellInfoQueryVariables,
} from 'notebook/structured-tables/row-updaters/__generated/BulkInventoryPlateWellInfoQuery';
import {
  BulkPlateSizeQuery,
  BulkPlateSizeQueryVariables,
} from 'notebook/structured-tables/row-updaters/__generated/BulkPlateSizeQuery';
import {BulkPlateWellInfoQuery} from 'notebook/structured-tables/row-updaters/__generated/BulkPlateWellInfoQuery';
import {BULK_PLATE_SIZE_QUERY} from 'notebook/structured-tables/row-updaters/TransferStructuredTableRowUpdater';
import {
  PlatePositionLookupInfo,
  PrimarySampleLookupType,
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import isColumnForSchemaField from 'notebook/structured-tables/util/isColumnForSchemaField';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {
  convertTagToTypedValue,
  getTypedValueFromBioentitySummary,
  getTypedValueFromStorableSummaries,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {InvalidPositionError, parseGridPosition, Position} from 'samples/SampleUtil';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

type ContainerInfo = ExtractByTypename<
  BulkInventoryContainerInfoQuery['containers'][number],
  'ContainerNode'
>;

type ContainerContent = ContainerInfo['contents'][number];

type ParentStorableOrInaccessible = ContainerInfo['parentStorable'];

type ContainedItem = ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>;

type PlateCellInfo = ExtractByTypename<
  BulkInventoryPlateWellInfoQuery['directStorables'][number],
  'PlateNode'
>;

type PlateInfo = ExtractByTypename<BulkPlateSizeQuery['plates'][number], 'PlateNode'>;

type GridSchemaInfo = PlateInfo['storableSchema'];

type PlateSchemaInfo = ExtractByTypename<GridSchemaInfo, 'PlateSchemaNode'>;

interface ContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

export const BULK_INVENTORY_PLATE_WELL_INFO_QUERY = gql`
  # contents does not query id
  # eslint-disable benchlint/id-field-required
  query BulkInventoryPlateWellInfoQuery($inputs: [StorableInput!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    directStorables(inputs: $inputs) {
      ... on StorableInterface {
        id
      }
      ... on PlateNode {
        id
        name
        cell {
          ...GridCellIndexesGraphQL
          container {
            ... on ContainerNode {
              id
              tagsJson
              quantityJson
              contents {
                concentrationJson
                containedItem {
                  ... on RegistryItemInterface {
                    id
                    name
                    displayLabel
                  }
                  ...InaccessibleInfoGraphQL
                }
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
        parentStorable {
          ... on StorableInterface {
            id
            name
            validationState {
              id
              status
            }
          }
          ...InaccessibleInfoGraphQL
        }
        storableSchema {
          ... on StorableSchemaInterface {
            id
          }
        }
      }
      ...HasArchiveRecordGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('GridCellIndexes').fragment}
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_INVENTORY_CONTAINER_INFO_QUERY = gql`
  query BulkInventoryContainerInfoQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ...InventoryContainerLiveDataGraphQL
      ...HasArchiveRecordGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InventoryContainerLiveData').fragment}
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const isPlateCellInfoOrInaccessible = (
  s: BulkPlateWellInfoQuery['directStorables'][number]
): s is PlateCellInfo | InaccessibleInfoGraphQL =>
  ['PlateNode', 'InacessibleInfoNode'].includes(s.__typename);

export type InventoryTableModelInfo = PlateCellInfo | ContainerInfo;

function isPlateCellInfo(
  inventoryTableModelInfo: InventoryTableModelInfo | PlateInfo
): inventoryTableModelInfo is PlateCellInfo {
  return inventoryTableModelInfo.__typename === 'PlateNode';
}

function isContainerInfo(
  inventoryTableModelInfo: InventoryTableModelInfo
): inventoryTableModelInfo is ContainerInfo {
  return inventoryTableModelInfo.__typename === 'ContainerNode';
}

function isPlateSchemaInfo(plateSchema: GridSchemaInfo): plateSchema is PlateSchemaInfo {
  return plateSchema.__typename === 'PlateSchemaNode';
}

function parsePlateWellCoordinates(
  plateSchema: PlateSchemaInfo,
  plateWellPosition: Position | string
): Position {
  if (typeof plateWellPosition === 'string') {
    try {
      return parseGridPosition({
        position: plateWellPosition,
        gridHeight: plateSchema.height,
        gridWidth: plateSchema.width,
      });
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        throw new Error('Position should be valid. Well has already been updated');
      } else {
        throw e;
      }
    }
  }
  return plateWellPosition;
}

function contentsToBioentityCell(contents: Array<ContainerContent>): LiveCellValue {
  const containedItems = getNonBatchContainerContents<ContainerContent, ContentWithoutBatches>(contents).map(
    (content) => content.containedItem
  );
  // TODO(PLAN-1130) Better handling for inaccessible contents
  if (containedItems.some((containedItem) => isInaccessible(containedItem))) {
    return 'NO_VALUE';
  }
  const accessibleContainedItems = assertAllAccessible(containedItems);
  return {
    type: BaseTypedValueType.ARRAY,
    rawValue: accessibleContainedItems.map((containedItem) =>
      getTypedValueFromBioentitySummary(containedItem)
    ),
  };
}

function updateBioentityCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    return contentsToBioentityCell(model.contents);
  } else if (isPlateCellInfo(model) && !isInaccessible(model.cell?.container)) {
    return contentsToBioentityCell(model.cell?.container.contents ?? []);
  }
  return 'NO_VALUE';
}

function updateContainersCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    return getTypedValueFromStorableSummaries([model]);
  }
  return 'NO_VALUE';
}

function updatePlateCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isPlateCellInfo(model)) {
    return getTypedValueFromStorableSummaries([model]);
  }
  return 'NO_VALUE';
}

function parentStorableToTypedValue(parent: ParentStorableOrInaccessible | null): LiveCellValue {
  if (parent == null) {
    return getTypedValueFromStorableSummaries([]);
  }
  if (isInaccessible(parent)) {
    return 'NO_VALUE';
  }
  return getTypedValueFromStorableSummaries([parent]);
}

function updateParentStorableCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isContainerInfo(model) || isPlateCellInfo(model)) {
    return parentStorableToTypedValue(model.parentStorable);
  }
  return 'NO_VALUE';
}

function updatePositionCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    if (model.gridCell != null && !isInaccessible(model.gridCell)) {
      return {
        type: BaseTypedValueType.GRID_COORDINATES,
        gridCoordinates: {columnIndex: model.gridCell.columnIndex, rowIndex: model.gridCell.rowIndex},
      };
    } else {
      return null;
    }
  }
  return 'NO_VALUE';
}

function getUnitsTypedValue(measurement: Optional<LegacyMeasurement>): LiveCellValue {
  if (measurement == null || measurement.units == null) {
    return null;
  }
  return toStringTypedValue(measurement.units);
}

function updateQuantityUnitCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    const quantity: Optional<LegacyMeasurement> = model.quantityJson;
    return getUnitsTypedValue(quantity);
  } else if (isPlateCellInfo(model) && !isInaccessible(model.cell?.container)) {
    const quantity: Optional<LegacyMeasurement> = model.cell?.container.quantityJson;
    return getUnitsTypedValue(quantity);
  }
  return 'NO_VALUE';
}

function updateConcentrationUnitCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    const concentrations: Optional<Array<LegacyMeasurement>> = model.contents.map(
      (content) => content.concentrationJson
    );
    return concentrations.length === 1 ? getUnitsTypedValue(concentrations[0]) : 'NO_VALUE';
  } else if (isPlateCellInfo(model) && !isInaccessible(model.cell?.container)) {
    const concentrations: Optional<Array<LegacyMeasurement>> = model.cell?.container.contents.map(
      (content) => content.concentrationJson
    );
    return concentrations && concentrations.length === 1 ? getUnitsTypedValue(concentrations[0]) : 'NO_VALUE';
  }
  return 'NO_VALUE';
}

function getMeasurementValueTypedValue(measurement: Optional<LegacyMeasurement>): LiveCellValue {
  if (measurement == null || measurement.value == null) {
    return null;
  }
  return toStringTypedValue(measurement.value.toString());
}

function updateQuantityCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    const quantity: Optional<LegacyMeasurement> = model.quantityJson;
    return getMeasurementValueTypedValue(quantity);
  } else if (isPlateCellInfo(model) && !isInaccessible(model.cell?.container)) {
    const quantity: Optional<LegacyMeasurement> = model.cell?.container.quantityJson;
    return getMeasurementValueTypedValue(quantity);
  }
  return 'NO_VALUE';
}

function updateConcentrationCell(model: InventoryTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    const concentrations: Optional<Array<LegacyMeasurement>> = model.contents.map(
      (content) => content.concentrationJson
    );
    return concentrations.length === 1 ? getMeasurementValueTypedValue(concentrations[0]) : 'NO_VALUE';
  } else if (isPlateCellInfo(model) && !isInaccessible(model.cell?.container)) {
    const concentrations: Optional<Array<LegacyMeasurement>> = model.cell?.container.contents.map(
      (content) => content.concentrationJson
    );
    return concentrations && concentrations.length === 1
      ? getMeasurementValueTypedValue(concentrations[0])
      : 'NO_VALUE';
  }
  return 'NO_VALUE';
}

function updateTagValueCell(model: InventoryTableModelInfo, column: StructuredTableColumn): LiveCellValue {
  if (isContainerInfo(model)) {
    const matchingTag = model.tagsJson.find((tag: SchemaTag) =>
      isColumnForSchemaField(column, tag.tagSchemaField)
    );
    if (matchingTag) {
      return convertTagToTypedValue(matchingTag);
    }
  } else if (isPlateCellInfo(model)) {
    if (model.cell != null && !isInaccessible(model.cell.container)) {
      const matchingTag = model.cell.container.tagsJson.find((tag: SchemaTag) =>
        isColumnForSchemaField(column, tag.tagSchemaField)
      );
      if (matchingTag) {
        return convertTagToTypedValue(matchingTag);
      }
    }
  }
  return 'NO_VALUE';
}

function hasSchemaMismatch(model: InventoryTableModelInfo, column: StructuredTableColumn): boolean {
  const storableSchemaId = model.storableSchema.id;
  if (isContainersStructuredTableColumn(column)) {
    return (
      column.frontEndMetadata?.containerSchemaId != null &&
      column.frontEndMetadata?.containerSchemaId !== storableSchemaId
    );
  } else if (
    isExistingBioentityStructuredTableColumn(column) ||
    isMeasurementAmountStructuredTableColumn(column) ||
    isParentStorableStructuredTableColumn(column) ||
    isPositionStructuredTableColumn(column) ||
    isTagValueStructuredTableColumn(column) ||
    isUnitSelectorStructuredTableColumn(column)
  ) {
    return (
      column.frontEndMetadata?.storableSchemaId != null &&
      column.frontEndMetadata?.storableSchemaId !== storableSchemaId
    );
  }
  return false;
}

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<InventoryTableModelInfo | InaccessibleInfoGraphQL>> {
  const containerIds = sampleLookupInfos
    .filter((info) => APIUtil.isContainer(info.idForLookup))
    .map((info) => info.idForLookup);
  const containerPromise = doQuerySafe<
    BulkInventoryContainerInfoQuery,
    BulkInventoryContainerInfoQueryVariables
  >(
    {query: BULK_INVENTORY_CONTAINER_INFO_QUERY},
    {variables: {containerIds}, fetchPolicy: 'network-only'}
  ).then((response) => response.containers);

  const plateInfos = sampleLookupInfos.filter(
    (info): info is PlatePositionLookupInfo =>
      info.sampleType === PrimarySampleLookupType.PLATE_POSITION_DUPLE && info.plateWellPosition != null
  );
  const plateIds = plateInfos.map((info) => info.idForLookup);
  const wellPromise = doQuerySafe<BulkPlateSizeQuery, BulkPlateSizeQueryVariables>(
    {query: BULK_PLATE_SIZE_QUERY},
    {variables: {plateIds}, fetchPolicy: 'network-only'}
  ).then((response) =>
    Promise.resolve(
      response.plates.map(
        (
          plate,
          index
        ):
          | {isInaccessible: true; value: InaccessibleInfoGraphQL}
          | {isInaccessible: false; value: StorableInput} => {
          if (isInaccessible(plate)) {
            return {isInaccessible: true, value: plate};
          } else {
            assert(isPlateCellInfo(plate), 'Plate size query should have returned a plate');

            const plateSchema = plate.storableSchema;
            assert(isPlateSchemaInfo(plateSchema), 'Plate size query should have returned a plate schema');

            const {plateWellPosition} = plateInfos[index];
            assert(
              plateWellPosition != null,
              'PlateWellPosition must be specified when fetching plate well details for inventory table'
            );

            const {rowIndex, columnIndex} = parsePlateWellCoordinates(plateSchema, plateWellPosition);
            return {isInaccessible: false, value: {rowIndex, colIndex: columnIndex, id: plate.id}};
          }
        }
      )
    ).then(
      async (
        inaccessiblesOrInputs: Array<
          | {isInaccessible: true; value: InaccessibleInfoGraphQL}
          | {
              isInaccessible: false;
              value: StorableInput;
            }
        >
      ) => {
        const inputs = inaccessiblesOrInputs
          .filter((iOI): iOI is {isInaccessible: false; value: StorableInput} => !iOI.isInaccessible)
          .map((iOI) => iOI.value);
        const {directStorables} = await doQuerySafe<
          BulkInventoryPlateWellInfoQuery,
          BulkInventoryPlateWellInfoQueryVariables
        >({query: BULK_INVENTORY_PLATE_WELL_INFO_QUERY}, {variables: {inputs}, fetchPolicy: 'network-only'});

        assert(
          directStorables.every((s): s is PlateCellInfo | InaccessibleInfoGraphQL =>
            isPlateCellInfoOrInaccessible(s)
          ),
          'Expected only plates or inaccessible from BULK_INVENTORY_PLATE_WELL_INFO_QUERY'
        );
        return [
          ...directStorables,
          ...inaccessiblesOrInputs
            .filter(
              (iOI): iOI is {isInaccessible: true; value: InaccessibleInfoGraphQL} => iOI.isInaccessible
            )
            .map((iOI) => iOI.value),
        ];
      }
    )
  );

  const [containers, wells] = await Promise.all([containerPromise, wellPromise]);
  return [...containers, ...wells];
}

export const InventoryTableRowUpdater: StructuredTableRowUpdater<
  InventoryTableModelInfo,
  InventoryTableRowMetadata
> = {
  fetchModel(sampleLookupInfo: SampleLookupInfo): Promise<InventoryTableModelInfo | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<InventoryTableModelInfo | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache(sampleLookupInfo: SampleLookupInfo): InventoryTableModelInfo | null {
    const {idForLookup} = sampleLookupInfo;
    if (APIUtil.isContainer(idForLookup)) {
      const data = readQuerySafe<BulkInventoryContainerInfoQuery, BulkInventoryContainerInfoQueryVariables>(
        {query: BULK_INVENTORY_CONTAINER_INFO_QUERY},
        {variables: {containerIds: [idForLookup]}, fetchPolicy: 'cache-only'}
      );
      if (data == null || !data.containers?.[0] || isInaccessible(data.containers[0])) {
        return null;
      }
      return data.containers[0];
    }

    // If the model is not a container, it must be a plate well.
    assert(
      sampleLookupInfo.sampleType === PrimarySampleLookupType.PLATE_POSITION_DUPLE,
      'Since model is plate well, plate position duple should be provided.',
      {
        sampleLookupInfo,
      }
    );

    const plateSizeResponse = readQuerySafe<BulkPlateSizeQuery, BulkPlateSizeQueryVariables>(
      {query: BULK_PLATE_SIZE_QUERY},
      {variables: {plateIds: [idForLookup]}, fetchPolicy: 'cache-only'}
    );
    if (plateSizeResponse == null) {
      return null;
    }
    const plate = plateSizeResponse.plates?.[0];

    if (isInaccessible(plate)) {
      return null;
    }

    const plateSchema = plate.storableSchema;
    assert(isPlateSchemaInfo(plateSchema), 'Plate size query should have returned a plate schema');

    const {plateWellPosition} = sampleLookupInfo;
    assert(
      plateWellPosition != null,
      'PlateWellPosition must be specified when fetching plate well details for inventory table'
    );
    const {rowIndex, columnIndex} = parsePlateWellCoordinates(plateSchema, plateWellPosition);

    const plateWellQueryResponse = readQuerySafe<
      BulkInventoryPlateWellInfoQuery,
      BulkInventoryPlateWellInfoQueryVariables
    >(
      {query: BULK_INVENTORY_PLATE_WELL_INFO_QUERY},
      {
        variables: {
          inputs: [
            {
              id: idForLookup,
              rowIndex,
              colIndex: columnIndex,
            },
          ],
        },
        fetchPolicy: 'cache-only',
      }
    );

    if (
      plateWellQueryResponse == null ||
      !plateWellQueryResponse.directStorables?.[0] ||
      isInaccessible(plateWellQueryResponse.directStorables[0])
    ) {
      return null;
    }

    assert(
      isPlateCellInfoOrInaccessible(plateWellQueryResponse.directStorables[0]),
      'Expected only plate or inaccessible from INVENTORY_PLATE_WELL_INFO_QUERY'
    );
    return plateWellQueryResponse.directStorables[0];
  },

  updateCell(
    model: InventoryTableModelInfo | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue {
    if (isInaccessible(model)) {
      return 'NO_VALUE';
    }

    if (hasSchemaMismatch(model, column)) {
      return 'NO_VALUE';
    }

    const {columnType} = column;
    switch (columnType) {
      case TableColumnType.EXISTING_BIOENTITY:
        return updateBioentityCell(model);
      case TableColumnType.CONTAINERS:
        return updateContainersCell(model);
      case TableColumnType.PLATE:
        return updatePlateCell(model);
      case TableColumnType.PARENT_STORABLE:
        return updateParentStorableCell(model);
      case TableColumnType.POSITION:
        return updatePositionCell(model);
      case TableColumnType.UNIT_SELECTOR:
        return column.columnGroup === StructuredTableColumnGroups.CONCENTRATION
          ? updateConcentrationUnitCell(model)
          : updateQuantityUnitCell(model);
      case TableColumnType.MEASUREMENT_AMOUNT:
        if (column.columnGroup === StructuredTableColumnGroups.CONCENTRATION) {
          return updateConcentrationCell(model);
        } else if (column.columnGroup === TransferTableColumnGroup.TARGET) {
          return 'NO_VALUE';
        } else {
          return updateQuantityCell(model);
        }
      case TableColumnType.TAG_VALUE:
        return updateTagValueCell(model, column);
      default:
        return 'NO_VALUE';
    }
  },

  updateRow(
    model: InventoryTableModelInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<InventoryTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<InventoryTableRowMetadata> {
    // Finalized rows will only be updated if unlockRowAfterSubmit is true; otherwise they are frozen at the
    // time of submission
    if (row.isFinalized && !row.metadata.unlockRowAfterSubmit) {
      if (isInaccessible(model)) {
        return row;
      }
      return {...row, lastSyncedArchivePurpose: model.archiveRecord?.purpose ?? null};
    }

    if (isInaccessible(model)) {
      return row;
    }

    const cells = row.cells.map((cell, columnIndex) => {
      const column = columns[columnIndex];
      // Prevent live values in unfinalized rows being "burned into" the table on refresh, causing them to
      // persist after a container is removed. Instead, clear them out here & custom eval will
      // populate them
      const liveValue =
        !row.isFinalized && shouldIgnoreColumnForUpdate(column.columnType, column.columnGroup)
          ? null
          : this.updateCell(model, columnGroup, column);
      return updateCellWithLiveValue(cell, liveValue, {setCellBlankIfLiveValueNull: true});
    });

    return {
      ...row,
      lastSyncedArchivePurpose: model.archiveRecord?.purpose ?? null,
      cells,
    };
  },
};

/**
 * Helper method for identifying Inventory table-specific columns that should be cleared but not
 * populated with live data when updateRow is called.
 *
 * @returns true iff the live data update should be skipped for the column
 */
function shouldIgnoreColumnForUpdate(
  columnType: TableColumnType,
  columnGroup: ColumnGroup | undefined
): boolean {
  const ignorableColumnGroups: Array<ColumnGroup> = [
    StructuredTableColumnGroups.QUANTITY,
    StructuredTableColumnGroups.CONCENTRATION,
  ];
  if (columnType === TableColumnType.TAG_VALUE) {
    return true;
  }
  if (
    columnGroup != null &&
    ignorableColumnGroups.includes(columnGroup) &&
    [
      TableColumnType.MEASUREMENT_AMOUNT,
      TableColumnType.UNIT_SELECTOR,
      TableColumnType.UNIT_REFERENCE,
    ].includes(columnType)
  ) {
    return true;
  }

  return false;
}
